import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell, GoogleIcon } from "@/features/auth/AuthShell";
import { applySignedInUser, metaQuery, safeRedirect, toAuthUser } from "@/features/auth/session";
import { authClient } from "@/lib/auth-client";

const searchSchema = z.object({
  redirect: z.string().optional().catch(undefined),
  /** Set by Better Auth when an OAuth sign-in fails (e.g. not invited). */
  error: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/login")({
  validateSearch: (s) => searchSchema.parse(s),
  beforeLoad: ({ context, search }) => {
    if (context.user) throw redirect({ href: safeRedirect(search.redirect) });
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(metaQuery()),
  component: LoginPage,
});

function LoginPage() {
  const search = Route.useSearch();
  const { data: meta } = useQuery(metaQuery());
  const qc = useQueryClient();
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(
    search.error ? oauthErrorMessage(search.error) : null,
  );
  const [pending, setPending] = useState(false);
  const destination = safeRedirect(search.redirect);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    const { data, error: err } = await authClient.signIn.email({ email, password });
    setPending(false);
    if (err || !data?.user) {
      setError(err?.message ?? "Sign-in failed");
      return;
    }
    await applySignedInUser(qc, toAuthUser(data.user));
    await router.invalidate();
    await router.navigate({ href: destination });
  };

  const google = () =>
    authClient.signIn.social({
      provider: "google",
      callbackURL: destination,
      errorCallbackURL: "/login",
    });

  return (
    <AuthShell
      title="Sign in"
      subtitle={meta && !meta.hasUsers ? "No account exists yet. Create the first one." : undefined}
      footer={
        <>
          {meta && !meta.hasUsers ? "First time here?" : "Invited?"}{" "}
          <Link
            to="/signup"
            search={{ redirect: search.redirect }}
            className="text-foreground underline underline-offset-4"
          >
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4" data-testid="login-form">
        <div className="grid gap-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password"
          />
        </div>
        {error && (
          <p
            className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
            role="alert"
            data-testid="auth-error"
          >
            {error}
          </p>
        )}
        <Button type="submit" disabled={pending} data-testid="login-submit">
          Sign in
        </Button>
        {meta?.googleEnabled && (
          <>
            <div className="relative text-center text-xs text-muted-foreground before:absolute before:inset-x-0 before:top-1/2 before:h-px before:bg-border">
              <span className="relative bg-background px-2">or</span>
            </div>
            <Button type="button" variant="outline" onClick={google} data-testid="login-google">
              <GoogleIcon />
              Continue with Google
            </Button>
          </>
        )}
      </form>
    </AuthShell>
  );
}

function oauthErrorMessage(code: string): string {
  if (/invite|forbidden|unable_to_create_user|signup_disabled/i.test(code)) {
    return "This roadmap tool is invite-only. Ask an admin to invite your Google email address.";
  }
  return `Sign-in failed (${code}).`;
}
